import React, { useState, useMemo, useEffect } from 'react';
import { Card, Table, Badge, Pagination, Form } from 'react-bootstrap';
import { FaSort, FaSortUp, FaSortDown, FaExclamationCircle } from 'react-icons/fa';
import moment from 'moment';

const BADGE_INSTRUMENTO = {
  'Público': 'primary',
  'Privado': 'warning',
  'Certidão': 'success',
};

const BADGE_STATUS = {
  vigente: { variant: 'success', label: 'Vigente' },
  alerta: { variant: 'warning', label: 'Alerta' },
  vencida: { variant: 'danger', label: 'Vencida' },
  indeterminada: { variant: 'secondary', label: 'Indeterminada' },
};

const RECORDS_PER_PAGE = 10;

const SORT_OPTIONS = [
  { value: 'outorgante_resumo', label: 'Outorgante' },
  { value: 'descricao_tipo_poder', label: 'Tipo Poder' },
  { value: 'data_lavratura', label: 'Data Lavratura' },
  { value: 'data_vencimento', label: 'Vencimento' },
];

const SortIcon = ({ campo, ordenacao }) => {
  if (ordenacao.campo !== campo) return <FaSort className="ml-1 text-muted" />;
  return ordenacao.direcao === 'asc' ? (
    <FaSortUp className="ml-1" />
  ) : (
    <FaSortDown className="ml-1" />
  );
};

const renderVencimento = (proc) => {
  if (proc.data_vencimento == null) {
    return <span>Indeterminada</span>;
  }

  const dataFmt = moment(proc.data_vencimento).format('DD/MM/YYYY');
  const hoje = moment();
  const venc = moment(proc.data_vencimento);
  const dias = venc.diff(hoje, 'days');

  if (proc.status_vigencia === 'vencida') {
    return (
      <span>
        {dataFmt} <small>(exp. há {Math.abs(dias)}d)</small>
      </span>
    );
  }
  if (proc.status_vigencia === 'alerta') {
    return (
      <span>
        {dataFmt} <small>({dias}d rest.)</small>
      </span>
    );
  }
  return <span>{dataFmt}</span>;
};

const MobileCard = ({ proc, onDetalhe }) => {
  const statusInfo = BADGE_STATUS[proc.status_vigencia] || { variant: 'secondary', label: proc.status_vigencia };

  return (
    <Card
      className="mb-2"
      style={{ cursor: 'pointer', fontSize: '13px' }}
      onClick={() => { onDetalhe(proc.codigo); }}
    >
      <Card.Body className="py-2 px-3">
        <div className="d-flex justify-content-between align-items-start mb-1">
          <strong className="d-block" style={{ fontSize: '14px' }}>
            {proc.outorgante_resumo || '-'}
          </strong>
          <Badge variant={statusInfo.variant}>{statusInfo.label}</Badge>
        </div>

        <div className="mb-2">
          <Badge variant={BADGE_INSTRUMENTO[proc.descricao_tipo_instrumento] || 'secondary'} className="mr-1">
            {proc.descricao_tipo_instrumento}
          </Badge>
          <Badge variant="info">
            {proc.descricao_tipo_poder}
          </Badge>
        </div>

        <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: '6px' }}>
          <div className="d-flex justify-content-between py-1">
            <span className="text-muted">Outorgados</span>
            <span>{proc.outorgado_resumo || '-'}</span>
          </div>
          <div className="d-flex justify-content-between py-1">
            <span className="text-muted">Lavrada</span>
            <span>{moment(proc.data_lavratura).format('DD/MM/YYYY')}</span>
          </div>
          <div className="d-flex justify-content-between py-1">
            <span className="text-muted">Vencimento</span>
            <span>{renderVencimento(proc)}</span>
          </div>
          <div className="d-flex justify-content-between py-1">
            <span className="text-muted">Substab.</span>
            <span>{proc.descricao_tipo_substabelecimento}</span>
          </div>
        </div>
      </Card.Body>
    </Card>
  );
};

export default function ProcuracaoTabela({
  procuracoes,
  ordenacao,
  onOrdenacao,
  onDetalhe,
}) {
  const [currentPage, setCurrentPage] = useState(1);
  const [isMobile, setIsMobile] = useState(false);
  const colunasSort = ['outorgante_resumo', 'descricao_tipo_poder', 'data_lavratura', 'data_vencimento'];

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 992px)');
    const handler = (e) => { setIsMobile(e.matches); };
    setIsMobile(mq.matches);
    mq.addEventListener('change', handler);
    return () => { mq.removeEventListener('change', handler); };
  }, []);

  const totalPages = Math.ceil(procuracoes.length / RECORDS_PER_PAGE);

  const registrosPaginados = useMemo(() => {
    const fim = currentPage * RECORDS_PER_PAGE;
    const inicio = fim - RECORDS_PER_PAGE;
    return procuracoes.slice(inicio, fim);
  }, [procuracoes, currentPage]);

  // Reset para pagina 1 quando muda a lista
  useEffect(() => {
    setCurrentPage(1);
  }, [procuracoes.length]);

  const handleSort = (campo) => {
    onOrdenacao({
      campo: campo,
      direcao:
        ordenacao.campo === campo && ordenacao.direcao === 'asc'
          ? 'desc'
          : 'asc',
    });
  };

  const renderHeader = (label, campo) => {
    const sortable = colunasSort.indexOf(campo) >= 0;
    if (!sortable) return <th className="small">{label}</th>;
    return (
      <th
        className="small"
        style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}
        onClick={() => { handleSort(campo); }}
      >
        {label}
        <SortIcon campo={campo} ordenacao={ordenacao} />
      </th>
    );
  };

  if (procuracoes.length === 0) {
    return (
      <Card className="shadow-sm">
        <Card.Body className="py-5" style={{ minHeight: 180 }}>
          <div className="text-center">
            <FaExclamationCircle size={48} className="text-muted mb-3" />
            <h5 className="text-muted">Nenhum registro encontrado.</h5>
            <p className="text-muted">
              Utilize os filtros acima para buscar procurações.
            </p>
          </div>
        </Card.Body>
      </Card>
    );
  }

  const paginacao = totalPages > 1 && (
    <div className="d-flex justify-content-center mt-3">
      <Pagination className="flex-wrap justify-content-center">
        <Pagination.First
          onClick={() => { setCurrentPage(1); }}
          disabled={currentPage === 1}
        />
        <Pagination.Prev
          onClick={() => { setCurrentPage(currentPage - 1); }}
          disabled={currentPage === 1}
        />
        {Array.from({ length: totalPages }, (_, i) => i).map((number) => (
          <Pagination.Item
            key={number}
            active={currentPage === number + 1}
            onClick={() => { setCurrentPage(number + 1); }}
          >
            {number + 1}
          </Pagination.Item>
        ))}
        <Pagination.Next
          onClick={() => { setCurrentPage(currentPage + 1); }}
          disabled={currentPage === totalPages}
        />
        <Pagination.Last
          onClick={() => { setCurrentPage(totalPages); }}
          disabled={currentPage === totalPages}
        />
      </Pagination>
    </div>
  );

  if (isMobile) {
    return (
      <>
        <div className="d-flex justify-content-between align-items-center mb-2">
          <small className="text-muted">{procuracoes.length} registro(s)</small>
          <Form.Control
            as="select"
            size="sm"
            style={{ width: 'auto', fontSize: '12px' }}
            value={ordenacao.campo + ':' + ordenacao.direcao}
            onChange={(e) => {
              const [campo, direcao] = e.target.value.split(':');
              onOrdenacao({ campo, direcao });
            }}
          >
            {SORT_OPTIONS.map((opt) => (
              <React.Fragment key={opt.value}>
                <option value={opt.value + ':asc'}>{opt.label} ↑</option>
                <option value={opt.value + ':desc'}>{opt.label} ↓</option>
              </React.Fragment>
            ))}
          </Form.Control>
        </div>

        {registrosPaginados.map((proc) => (
          <MobileCard key={proc.codigo} proc={proc} onDetalhe={onDetalhe} />
        ))}

        {paginacao}
      </>
    );
  }

  return (
    <>
      <Card className="shadow-sm">
        <Card.Header
          style={{ backgroundColor: '#0747a6', color: '#fff' }}
          className="py-2 d-flex justify-content-between align-items-center"
        >
          <strong className="small">Procurações</strong>
          <small style={{ opacity: 0.8 }}>{procuracoes.length} registro(s)</small>
        </Card.Header>
        <div className="table-responsive">
          <Table striped bordered hover size="sm" className="mb-0">
            <thead>
              <tr>
                {renderHeader('Outorgante', 'outorgante_resumo')}
                <th className="small">Instrumento</th>
                {renderHeader('Tipo Poder', 'descricao_tipo_poder')}
                <th className="small">Outorgados</th>
                {renderHeader('Lavrada', 'data_lavratura')}
                {renderHeader('Vencimento', 'data_vencimento')}
                <th className="small">Status</th>
                <th className="small">Tabeliao</th>
                <th className="small">Substab.</th>
              </tr>
            </thead>
            <tbody>
              {registrosPaginados.map((proc) => {
                const statusInfo = BADGE_STATUS[proc.status_vigencia] || { variant: 'secondary', label: proc.status_vigencia };
                return (
                  <tr
                    key={proc.codigo}
                    style={{ cursor: 'pointer' }}
                    onClick={() => { onDetalhe(proc.codigo); }}
                  >
                    <td className="small">{proc.outorgante_resumo || '-'}</td>
                    <td>
                      <Badge variant={BADGE_INSTRUMENTO[proc.descricao_tipo_instrumento] || 'secondary'}>
                        {proc.descricao_tipo_instrumento}
                      </Badge>
                    </td>
                    <td className="small">{proc.descricao_tipo_poder}</td>
                    <td className="small">{proc.outorgado_resumo || '-'}</td>
                    <td className="small">
                      {moment(proc.data_lavratura).format('DD/MM/YYYY')}
                    </td>
                    <td className="small">{renderVencimento(proc)}</td>
                    <td className="text-center">
                      <Badge variant={statusInfo.variant}>{statusInfo.label}</Badge>
                    </td>
                    <td className="small">{proc.tabeliao_nome || '-'}</td>
                    <td className="small">{proc.descricao_tipo_substabelecimento}</td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </div>
      </Card>

      {paginacao}
    </>
  );
}
